import type { I18nDictionary } from "../core/types";

export type OpenSNDictionaryExtension = Partial<{
  social: Partial<{
    ui: Record<string, string>;
    modules: Record<string, string>;
  }>;
  public: Partial<{
    nav: Record<string, string>;
    hero: Record<string, string>;
  }>;
  auth: Record<string, string>;
  categoryLabels: Record<string, string>;
  errors: Record<string, string>;
  errorMessages: Record<string, string>;
  support: Record<string, string>;
}> & Record<string, unknown>;

export type OpenSNEmailTemplateId =
  | "passwordReset"
  | "passwordChanged"
  | "welcome"
  | "emailVerification"
  | "friendRequest"
  | "eventInvite";

export type OpenSNEmailTemplateVariables = Record<string, string | number | boolean | null | undefined>;

export type OpenSNRenderedEmailTemplate = {
  subject: string;
  htmlContent: string;
  textContent: string;
};

type OpenSNEmailTemplateDefinition = {
  subject: string;
  greeting: string;
  paragraphs: string[];
  actionLabel?: string;
  footer: string;
};

export const OPEN_SN_EMAIL_TEMPLATES: Record<string, Record<OpenSNEmailTemplateId, OpenSNEmailTemplateDefinition>> = {
  en: {
    passwordReset: {
      subject: "Reset your {{appName}} password",
      greeting: "Hi {{displayName}},",
      paragraphs: [
        "Use the button below to choose a new password for your {{appName}} account.",
        "This link expires soon. If you did not request a password reset, you can ignore this email."
      ],
      actionLabel: "Reset password",
      footer: "This email was sent by {{appName}}."
    },
    passwordChanged: {
      subject: "Your {{appName}} password was changed",
      greeting: "Hi {{displayName}},",
      paragraphs: [
        "The password for your {{appName}} account was changed.",
        "If this was not you, contact support immediately."
      ],
      footer: "This email was sent by {{appName}}."
    },
    welcome: {
      subject: "Welcome to {{appName}}",
      greeting: "Hi {{displayName}},",
      paragraphs: [
        "Your {{appName}} account is ready.",
        "You can now complete your profile and start using the social network."
      ],
      actionLabel: "Open {{appName}}",
      footer: "This email was sent by {{appName}}."
    },
    emailVerification: {
      subject: "Confirm your {{appName}} email address",
      greeting: "Hi {{displayName}},",
      paragraphs: [
        "Confirm this email address to finish securing your {{appName}} account."
      ],
      actionLabel: "Confirm email",
      footer: "This email was sent by {{appName}}."
    },
    friendRequest: {
      subject: "{{actorName}} sent you a friend request on {{appName}}",
      greeting: "Hi {{displayName}},",
      paragraphs: [
        "{{actorName}} wants to connect with you on {{appName}}."
      ],
      actionLabel: "Review request",
      footer: "This email was sent by {{appName}}."
    },
    eventInvite: {
      subject: "You were invited to {{eventName}}",
      greeting: "Hi {{displayName}},",
      paragraphs: [
        "{{actorName}} invited you to {{eventName}} on {{appName}}."
      ],
      actionLabel: "View event",
      footer: "This email was sent by {{appName}}."
    }
  },
  ro: {
    passwordReset: {
      subject: "Reseteaza parola {{appName}}",
      greeting: "Salut, {{displayName}},",
      paragraphs: [
        "Foloseste butonul de mai jos ca sa alegi o parola noua pentru contul tau {{appName}}.",
        "Linkul expira in scurt timp. Daca nu ai cerut resetarea parolei, poti ignora acest email."
      ],
      actionLabel: "Reseteaza parola",
      footer: "Acest email a fost trimis de {{appName}}."
    },
    passwordChanged: {
      subject: "Parola ta {{appName}} a fost schimbata",
      greeting: "Salut, {{displayName}},",
      paragraphs: [
        "Parola contului tau {{appName}} a fost schimbata.",
        "Daca nu tu ai facut aceasta modificare, contacteaza imediat suportul."
      ],
      footer: "Acest email a fost trimis de {{appName}}."
    },
    welcome: {
      subject: "Bun venit in {{appName}}",
      greeting: "Salut, {{displayName}},",
      paragraphs: [
        "Contul tau {{appName}} este pregatit.",
        "Acum iti poti completa profilul si poti folosi reteaua sociala."
      ],
      actionLabel: "Deschide {{appName}}",
      footer: "Acest email a fost trimis de {{appName}}."
    },
    emailVerification: {
      subject: "Confirma adresa de email {{appName}}",
      greeting: "Salut, {{displayName}},",
      paragraphs: [
        "Confirma aceasta adresa de email ca sa finalizezi securizarea contului tau {{appName}}."
      ],
      actionLabel: "Confirma emailul",
      footer: "Acest email a fost trimis de {{appName}}."
    },
    friendRequest: {
      subject: "{{actorName}} ti-a trimis o cerere de prietenie pe {{appName}}",
      greeting: "Salut, {{displayName}},",
      paragraphs: [
        "{{actorName}} vrea sa se conecteze cu tine pe {{appName}}."
      ],
      actionLabel: "Vezi cererea",
      footer: "Acest email a fost trimis de {{appName}}."
    },
    eventInvite: {
      subject: "Ai fost invitat la {{eventName}}",
      greeting: "Salut, {{displayName}},",
      paragraphs: [
        "{{actorName}} te-a invitat la {{eventName}} pe {{appName}}."
      ],
      actionLabel: "Vezi evenimentul",
      footer: "Acest email a fost trimis de {{appName}}."
    }
  }
};

export function renderOpenSNEmailTemplate(templateId: OpenSNEmailTemplateId, localeCode: string, variables: OpenSNEmailTemplateVariables = {}): OpenSNRenderedEmailTemplate {
  const template = resolveEmailTemplate(templateId, localeCode);
  const values: OpenSNEmailTemplateVariables = { appName: "OpenSN", displayName: "", actorName: "", eventName: "", ...variables };
  const subject = interpolateEmailText(template.subject, values);
  const greeting = interpolateEmailText(template.greeting, values);
  const paragraphs = template.paragraphs.map((paragraph) => interpolateEmailText(paragraph, values));
  const footer = interpolateEmailText(template.footer, values);
  const actionLabel = template.actionLabel ? interpolateEmailText(template.actionLabel, values) : "";
  const actionUrl = String(values.actionUrl ?? values.resetUrl ?? values.verificationUrl ?? values.url ?? "").trim();
  const textParts = [greeting, ...paragraphs];
  if (actionUrl) textParts.push(actionLabel ? `${actionLabel}: ${actionUrl}` : actionUrl);
  textParts.push(footer);
  return {
    subject,
    textContent: textParts.filter(Boolean).join("\n\n"),
    htmlContent: emailTemplateHtml(subject, greeting, paragraphs, actionUrl, actionLabel, footer)
  };
}

function resolveEmailTemplate(templateId: OpenSNEmailTemplateId, localeCode: string): OpenSNEmailTemplateDefinition {
  const normalized = localeCode.toLowerCase();
  const shortLocale = normalized.split("-")[0] ?? normalized;
  return (OPEN_SN_EMAIL_TEMPLATES[normalized] ?? OPEN_SN_EMAIL_TEMPLATES[shortLocale] ?? OPEN_SN_EMAIL_TEMPLATES.en)[templateId];
}

function interpolateEmailText(text: string, variables: OpenSNEmailTemplateVariables): string {
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/gu, (_, key: string) => String(variables[key] ?? "").trim());
}

function emailTemplateHtml(subject: string, greeting: string, paragraphs: string[], actionUrl: string, actionLabel: string, footer: string): string {
  const paragraphHtml = paragraphs.map((paragraph) => `<p style="margin:0 0 16px">${escapeEmailHtml(paragraph)}</p>`).join("");
  const actionHtml = actionUrl ? `<p style="margin:24px 0"><a href="${escapeEmailAttribute(actionUrl)}" style="display:inline-block;background:#111827;color:#ffffff;text-decoration:none;border-radius:6px;padding:12px 18px;font-weight:700">${escapeEmailHtml(actionLabel || actionUrl)}</a></p><p style="margin:0 0 16px;color:#4b5563;word-break:break-word">${escapeEmailHtml(actionUrl)}</p>` : "";
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeEmailHtml(subject)}</title></head><body style="margin:0;background:#f3f4f6;color:#111827;font-family:Arial,Helvetica,sans-serif"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#f3f4f6"><tr><td align="center" style="padding:32px 16px"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:560px;background:#ffffff;border-radius:10px"><tr><td style="padding:32px"><p style="margin:0 0 16px">${escapeEmailHtml(greeting)}</p>${paragraphHtml}${actionHtml}<p style="margin:24px 0 0;color:#6b7280;font-size:13px">${escapeEmailHtml(footer)}</p></td></tr></table></td></tr></table></body></html>`;
}

function escapeEmailHtml(value: string): string {
  return value.replace(/[&<>"']/gu, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#039;" })[char] ?? char);
}

function escapeEmailAttribute(value: string): string {
  return escapeEmailHtml(value).replace(/`/gu, "&#096;");
}

const OPEN_SN_BASE_EN: I18nDictionary = {
  "localeCode": "en-US",
  "social": {
    "modules": {
      "ads": "Ads",
      "articles": "Articles",
      "chat": "Messenger",
      "clips": "Video",
      "documents": "Documents",
      "events": "Events",
      "feed": "Home",
      "groups": "Groups",
      "membership": "Membership",
      "network": "Friends",
      "notifications": "Notifications",
      "ops": "Admin",
      "pages": "Pages",
      "profile": "Profile",
      "search": "Search",
      "saved": "Saved",
      "settings": "Settings",
      "support": "Support",
      "wallet": "Wallet"
    },
    "ui": {
      "guestMember": "Guest",
      "currentProfile": "Current profile",
      "primaryNavigation": "Primary navigation",
      "socialSearch": "Search",
      "searchPlaceholder": "Search",
      "addNewPost": "New post",
      "writePostCta": "Write a post",
      "guestWelcomeTitle": "Welcome",
      "createAccount": "Create account",
      "invitePeople": "Invite people",
      "profile": "Profile",
      "logOut": "Log out",
      "invites": "Invites",
      "membership": "Membership",
      "verify": "Verify",
      "stories": "Stories",
      "events": "Events",
      "groups": "Groups",
      "pages": "Pages",
      "notifications": "Notifications",
      "clips": "Clips",
      "articles": "Articles",
      "documents": "Documents",
      "ads": "Ads",
      "lists": "Lists",
      "workCommunitySupport": "Work in Europe",
      "saferStartTitle": "OpenSN",
      "walletTools": "Wallet",
      "addFunds": "Add money",
      "withdrawFunds": "Withdraw money",
      "sendMoney": "Send money",
      "payNow": "Pay now",
      "languagesPrepared": "Search",
      "filterResults": "Search",
      "clear": "Clear",
      "cancel": "Cancel",
      "settingsSections": "Settings",
      "settingsProfile": "Profile",
      "settingsPrivacy": "Privacy",
      "settingsNotifications": "Notifications",
      "settingsEmail": "Email",
      "settingsPassword": "Password",
      "settingsTwoFactor": "Two factor",
      "settingsDisplay": "Display",
      "settingsDataDownload": "Data download",
      "settingsAccount": "Account",
      "settingsSubscriptions": "Subscriptions",
      "settingsCreatorDashboard": "Creator dashboard",
      "settingsRequireLoginTitle": "Log in to open settings",
      "settingsRequireLoginBody": "Your profile, privacy and account settings are saved to your account.",
      "logInToContinueTitle": "Log in to continue",
      "logInToContinueBody": "Use your account to post, message, save items and manage settings.",
      "adminSearchPlaceholder": "Search users, posts or records",
      "itemsPerPage": "Items per page",
      "page": "Page",
      "previousPage": "Previous",
      "nextPage": "Next",
      "mediaDefaults": "Media defaults",
      "mediaDefaultsHelp": "Set the fallback profile and cover images used when members have not uploaded their own.",
      "defaultProfilePhotoLight": "Default profile photo, light theme",
      "defaultProfilePhotoDark": "Default profile photo, dark theme",
      "defaultCoverPhotoLight": "Default cover photo, light theme",
      "defaultCoverPhotoDark": "Default cover photo, dark theme",
      "createdAt": "Created",
      "reason": "Reason",
      "going": "Going",
      "replies": "Replies",
      "pending": "Pending",
      "failed": "Failed",
      "expired": "Expired",
      "revoked": "Revoked",
      "accepted": "Accepted",
      "blocked": "Blocked",
      "archived": "Archived",
      "resolved": "Resolved",
      "escalated": "Escalated",
      "hashtags": "Hashtags",
      "followers": "Followers",
      "categories": "Categories",
      "sortOrder": "Sort order",
      "moduleFeedLead": "Posts, comments and updates from your community.",
      "moduleNetworkLead": "Invite contacts and see who joined you.",
      "moduleChatLead": "Private messages with people you know.",
      "moduleGroupsLead": "Join communities for arrival, jobs, documents, housing and rights in Europe.",
      "moduleEventsLead": "Find online and local events, respond and see who is going.",
      "modulePagesLead": "Find individual worker pages, work agencies, employers, companies and support services.",
      "moduleDocumentsLead": "Keep useful documents and shared resources in one place.",
      "moduleClipsLead": "Short videos from friends and pages.",
      "moduleSettingsLead": "Update your profile, privacy, notifications and account.",
      "moduleSupportLead": "Send a request when you need help with your account or safety.",
      "moduleWalletLead": "Manage balances, payments and transfers.",
      "moduleMembershipLead": "Choose a plan and manage your current membership.",
      "moduleSearchLead": "Search across OpenSN.",
      "moduleSavedLead": "Posts, links and media you saved.",
      "moduleNotificationsLead": "Friend requests, replies, messages and safety alerts appear here.",
      "moduleAdsLead": "Create and manage promotions for posts, pages and offers.",
      "profilePhotoRemoved": "Profile photo will be removed when you save.",
      "coverImageRemoved": "Cover image will be removed when you save.",
      "uploadProfilePhoto": "Uploading profile photo",
      "uploadProfileMedia": "Uploading profile media",
      "savingSettings": "Settings",
      "settingsSaved": "Settings",
      "settingsRequestFailed": "Settings: Support",
      "chooseImageFile": "Choose one image file.",
      "chooseCoverImageFile": "Choose one cover image file.",
      "changeProfilePhoto": "Change profile photo",
      "removeImage": "Remove image",
      "clearSelectedProfilePhoto": "Clear selected profile photo",
      "changeCoverImage": "Change cover image",
      "removeCover": "Remove cover",
      "clearSelectedCoverImage": "Clear selected cover image",
      "displayName": "Display name",
      "username": "Username",
      "gender": "Gender",
      "selectGender": "Select gender",
      "genderFemale": "Female",
      "genderMale": "Male",
      "genderNonBinary": "Non-binary",
      "genderSelfDescribed": "Self-described",
      "genderPreferNotToSay": "Prefer not to say",
      "bio": "Bio",
      "bioPlaceholder": "Find community, verified pages, jobs guidance, trusted support and practical local knowledge.",
      "currentCity": "Current city",
      "currentCityPlaceholder": "City in Europe",
      "workSector": "Work in Europe",
      "publicProfile": "Public profile",
      "profileVisibility": "Profile visibility",
      "messagesFrom": "Who can message you",
      "friendListVisibility": "Friends list visibility",
      "friendActivityVisibility": "Friendship activity visibility",
      "everyone": "Everyone",
      "nobody": "Nobody",
      "allowMessagesFromNetwork": "Chat: Friends",
      "allowCommentsOnPosts": "Allow comments on posts",
      "searchableProfile": "Show profile in search",
      "showOnlineStatus": "Show active status",
      "blockedUserId": "Blocked user",
      "optionalUserId": "Optional user ID",
      "emailDigest": "Email digest",
      "pushNotifications": "Notifications",
      "friendRequestNotifications": "Friend requests",
      "friendAcceptedNotifications": "Accepted friend requests",
      "friendActivityNotifications": "Friend activity",
      "mutualFriendNotifications": "Mutual friends",
      "friendCountOne": "friend",
      "friendCountMany": "friends",
      "mutualFriendCountOne": "mutual friend",
      "mutualFriendCountMany": "mutual friends",
      "commentNotifications": "Comments and replies",
      "messageNotifications": "Messages",
      "safetyAlerts": "Safety",
      "friendRequestReceivedNotification": "Friend request",
      "friendRequestAcceptedNotification": "Friend request accepted",
      "friendAddedFriendNotification": "New friendship",
      "friendAddedFriendBody": "is now friends with",
      "followNotification": "New follow",
      "notificationUnread": "Unread",
      "markAllRead": "Mark all as read",
      "email": "Email",
      "productUpdates": "Product updates",
      "communityDigest": "Community digest",
      "currentPassword": "Current password",
      "newPassword": "New password",
      "method": "Method",
      "phone": "Phone",
      "authenticatorApp": "Authenticator app",
      "verificationCode": "Verification code",
      "codeAfterSetup": "Code after setup",
      "language": "Language",
      "theme": "Theme",
      "themeLight": "Light",
      "themeDark": "Dark",
      "themeSystem": "System",
      "darkMode": "Dark mode",
      "autoplayMedia": "Autoplay media",
      "exportScope": "Export scope",
      "allAccountData": "All account data",
      "timeline": "Posts",
      "posts": "Posts",
      "media": "Media",
      "exportQueued": "Export queued",
      "sessions": "Sessions",
      "noActiveSessions": "No active sessions were returned.",
      "typeDelete": "Type DELETE to confirm",
      "deleteConfirmationToken": "DELETE",
      "confirmPassword": "Confirm password",
      "deleteReason": "Delete reason",
      "optionalReason": "Optional reason",
      "loginBeforeSubmitting": "Log in",
      "submitting": "Submitting",
      "saved": "Saved",
      "savedItems": "Saved items",
      "savedItem": "Saved item",
      "submitFailed": "Submit failed",
      "category": "Category",
      "loadingCategories": "Loading categories",
      "categoriesUnavailable": "Categories unavailable",
      "noCategoriesConfigured": "No categories configured",
      "chooseCategory": "Choose category",
      "allCategories": "All categories",
      "objective": "Objective",
      "traffic": "Traffic",
      "reach": "Reach",
      "message": "Message",
      "messages": "Messages",
      "budgetEur": "Budget EUR",
      "audience": "Audience",
      "audiencePlaceholder": "Countries, languages, work sectors",
      "creative": "Creative",
      "creativePlaceholder": "Campaign message",
      "recipientId": "Send to",
      "recipientPlaceholder": "username or email",
      "amountEur": "Amount in EUR",
      "paymentProvider": "Payment method",
      "uploadedFileId": "Video file",
      "uploadedFilePlaceholder": "Choose or paste the video file",
      "caption": "Caption",
      "clipCaption": "Clip caption",
      "title": "Title",
      "slug": "Custom link",
      "excerpt": "Excerpt",
      "body": "Body",
      "status": "Status",
      "draft": "Draft",
      "published": "Published",
      "name": "Name",
      "optionalSlug": "Optional custom link",
      "description": "Description",
      "privacy": "Privacy",
      "public": "Public",
      "private": "Private",
      "network": "Friends",
      "eventName": "Event name",
      "eventNamePlaceholder": "Add a clear event name",
      "eventFormat": "Event format",
      "inPersonEvent": "In person",
      "onlineEvent": "Online",
      "eventLocation": "Location",
      "eventLocationPlaceholder": "Venue, city or address",
      "eventLocationTbd": "Location to be announced",
      "eventLink": "Online event link",
      "eventLinkPlaceholder": "https://...",
      "eventDescriptionPlaceholder": "Tell people what will happen, who should come and what to expect.",
      "eventHosts": "Hosts",
      "eventHostsPlaceholder": "Host names or pages",
      "eventHostedBy": "Hosted by",
      "eventCapacity": "Capacity",
      "eventCapacityPlaceholder": "Optional limit",
      "eventInvitesPlaceholder": "Names, usernames or emails",
      "eventDateTbd": "TBD",
      "eventRsvp": "RSVP",
      "eventInterested": "Interested",
      "eventGoing": "Going",
      "eventCantGo": "Can't go",
      "eventRsvpSaving": "Saving response",
      "eventRsvpSaved": "Response saved",
      "eventRsvpFailed": "Response failed",
      "eventDetails": "Event details",
      "eventsUnavailable": "Events are not available yet.",
      "createEvent": "Create event",
      "playbackActive": "Playback active",
      "playbackActiveWithSound": "Playback active with sound",
      "playMedia": "Play media",
      "pauseMedia": "Pause media",
      "play": "Play",
      "pause": "Pause",
      "editProfile": "Edit profile",
      "uploadAvatar": "Upload avatar",
      "uploadCover": "Upload cover",
      "share": "Share",
      "linkReady": "Link ready",
      "refresh": "Refresh",
      "search": "Search",
      "loadMore": "Load more",
      "quickActions": "Quick actions",
      "done": "Done",
      "actionFailed": "Action failed",
      "copyingLink": "Copying link",
      "linkCopied": "Link copied",
      "loadingResults": "Loading results",
      "loginRequired": "Log in required",
      "loginToLoadView": "Log in to see this area.",
      "unableToLoadView": "We could not load this area.",
      "retry": "Retry",
      "goHome": "Go home",
      "contactSupport": "Contact support",
      "keepSubscription": "Keep subscription",
      "featurePage": "Feature page",
      "editItem": "Edit",
      "runNow": "Run now",
      "disconnect": "Disconnect",
      "installApp": "Install app",
      "boostPost": "Boost post",
      "logOutOtherDevices": "Log out other devices",
      "rows": "Rows",
      "queues": "Queues",
      "overview": "Overview",
      "users": "Users",
      "reports": "Reports",
      "follows": "Follows",
      "supportTickets": "Support Tickets",
      "openidProviders": "OpenID Providers",
      "mediaProcessing": "Media Processing",
      "dataExports": "Data Exports",
      "accountDeletes": "Account Deletes",
      "opsGroupOverview": "Overview",
      "opsGroupContent": "Content",
      "opsGroupCommunity": "Community",
      "opsGroupEngagement": "Engagement",
      "opsGroupCommunication": "Communication",
      "opsGroupModeration": "Moderation",
      "opsGroupMonetization": "Monetization",
      "opsGroupConfiguration": "Configuration",
      "opsGroupOperations": "Operations",
      "jobs": "Jobs",
      "deadLetters": "Dead letters",
      "cron": "Cron",
      "outbox": "Outbox",
      "webhooks": "Webhooks",
      "locks": "Locks",
      "exports": "Exports",
      "deletes": "Deletes",
      "retention": "Retention",
      "roles": "Roles",
      "section": "Section",
      "subject": "Subject",
      "describeWhatHappened": "Describe exactly what happened",
      "chooseFiles": "Choose one or more files to attach.",
      "attachFiles": "Attach files",
      "clearForm": "Clear form",
      "ticketId": "Ticket ID",
      "supportRequestFailed": "Support",
      "onboarding": "Onboarding",
      "working": "Working",
      "passwordResetComplete": "Password reset complete",
      "recoveryEmailQueued": "Check your email for the reset link",
      "logInFirstSocialConsent": "Log in first to connect another sign-in service.",
      "logInAfterSignupToFinishSetup": "Log in after creating your account to finish setup.",
      "requestFailed": "Request failed",
      "invalidCredentials": "The email or password is incorrect.",
      "startingOpenId": "Opening Google sign-in",
      "openIdProviderReady": "Google sign-in is ready",
      "openIdRequestFailed": "Google sign-in failed",
      "usernameMinLength": "Username must have at least 3 characters.",
      "checkingUsername": "Checking username",
      "usernameAlreadyUsed": "Username is already used.",
      "usernameAvailable": "Username is available.",
      "usernameCheckFailed": "Username check failed",
      "avatarRemovedOnboarding": "Avatar will be removed when you finish onboarding.",
      "originCountry": "Origin country",
      "currentCountry": "Current country",
      "interests": "Interests",
      "password": "Password",
      "resetPasswordWithToken": "New password",
      "enterPassword": "Enter your password",
      "hidePassword": "Hide password",
      "showPassword": "Show password",
      "resetToken": "Recovery codes",
      "check": "Check",
      "finishOnboarding": "Finish onboarding",
      "continue": "Continue",
      "forgotPassword": "Forgot password",
      "sendResetLink": "Send reset link",
      "resetPassword": "Reset password",
      "signUp": "Join",
      "alreadyHaveAccount": "Already have an account?",
      "skipOptionalSetup": "Skip optional setup",
      "profileSettings": "Profile settings",
      "backToLogin": "Back to login",
      "filterInvitedPeople": "Filter invited people",
      "invitedPeople": "Invited people",
      "invitedPeopleEmpty": "Invited people will appear here after you send an invite or copy a registration link.",
      "invitedPeopleShortEmpty": "Invited people will appear here.",
      "addEmailOrPhone": "Add an email address or phone number.",
      "sendingInvite": "Sending invite",
      "inviteQueuedLinkReady": "Invite queued and link ready",
      "inviteQueued": "Invite queued",
      "inviteRequestFailed": "Invite request failed",
      "creatingRegistrationLink": "Creating registration link",
      "inviteLinkMissing": "Invite link missing",
      "registrationLinkCopied": "Registration link copied",
      "inviteLinkRequestFailed": "Invite link request failed",
      "registrationLink": "Registration link",
      "sendInvite": "Send invite",
      "copyRegistrationLink": "Copy registration link",
      "copyLink": "Copy link",
      "acceptedBy": "Accepted by",
      "openProfile": "Open profile",
      "about": "About",
      "walletEmpty": "Wallet activity will appear after your first transaction.",
      "feedEmpty": "Your feed will fill with posts from friends, groups and pages.",
      "feedFilters": "Feed filters",
      "searchEmpty": "Use the field above to search.",
      "searchNoResultsTitle": "No results",
      "searchNoResultsBody": "We could not find anything for this search.",
      "notificationsEmpty": "Friend requests, replies, messages and safety alerts will appear here.",
      "savedEmpty": "Saved posts, links and media will appear here.",
      "noGroupsYet": "No groups yet",
      "noEventsYet": "No events yet",
      "noPagesYet": "No pages yet",
      "noFriendsYet": "No friends yet",
      "noDocumentsYet": "No documents yet",
      "noPhotosYet": "No photos yet",
      "noArticlesYet": "No articles yet",
      "groupsEmpty": "Work, arrival, documents, housing and safety groups matching this view will appear here.",
      "eventsEmpty": "Online and local events matching this view will appear here.",
      "pagesEmpty": "Individual worker, agency, employer and company pages matching this view will appear here.",
      "documentsEmpty": "Documents attached to posts will appear here.",
      "clipsEmpty": "Video clips will appear here after members publish videos.",
      "photos": "Photos",
      "photosEmpty": "Photos will appear here after they are uploaded.",
      "articlesEmpty": "Articles will appear here after members publish longer posts.",
      "friendsEmpty": "Friends will appear here after connections are accepted.",
      "listsEmpty": "Saved lists and social relationships will appear here.",
      "adsEmpty": "Campaigns and ad reports will appear here after setup.",
      "opsEmpty": "Operational records will appear here after queues, jobs or background processes run.",
      "profileEmpty": "Profile content will appear here after posts, media or interactions exist.",
      "profileAboutEmpty": "Profile details will appear here after they are added.",
      "profileTimelineEmpty": "Posts will appear here after they are published.",
      "profileNotFound": "Profile unavailable",
      "profileNotFoundBody": "This profile does not exist or is not available.",
      "membershipPlansEmpty": "No membership plans are available yet.",
      "membershipCurrentEmpty": "You do not have an active membership yet.",
      "membershipCheckoutEmpty": "Choose a membership plan before checkout.",
      "genericEmpty": "Nothing to show yet.",
      "selectPlan": "Select plan",
      "startCheckout": "Start checkout",
      "membershipCheckoutStarted": "Membership checkout started",
      "currentMembership": "Current membership",
      "noPostsYet": "No posts yet",
      "startWithQuery": "Start with a query",
      "noConversationsYet": "No conversations yet",
      "newMessage": "New message",
      "allClear": "All clear",
      "noActivityYet": "No activity yet",
      "nothingHereYet": "Nothing here yet",
      "open": "Open",
      "directoryItem": "Directory item",
      "ready": "Ready",
      "join": "Join",
      "follow": "Follow",
      "joined": "Joined",
      "following": "Following",
      "hashtagFollowed": "Hashtag followed",
      "termsAccepted": "I accept the Terms.",
      "privacyAccepted": "I accept the Privacy Policy.",
      "termsConsentIntro": "I agree to the",
      "termsConsentJoin": "and",
      "privacyPolicy": "Privacy Policy",
      "marketingConsentText": "I want product and marketing updates described in the",
      "cookies": "Cookies",
      "code": "Code",
      "methodId": "Method ID",
      "provider": "Sign-in service",
      "initialSetupSteps": "Initial setup steps",
      "recommendations": "Recommendations",
      "removeAvatar": "Remove avatar",
      "avatarWillBeRemoved": "Avatar will be removed",
      "clearSelectedAvatar": "Clear selected avatar",
      "headline": "Headline",
      "headlinePlaceholder": "Worker, student, founder, parent",
      "bioSupportPlaceholder": "Tell people what support, work or community you are looking for",
      "languages": "Languages",
      "interestsAndHashtags": "Interests and hashtags",
      "logIn": "Log in",
      "continueWithOpenId": "Continue with Google",
      "emailToken": "Confirmation token",
      "requestCompleted": "Request completed",
      "createSupportTicket": "Send support request",
      "ticketCreated": "Ticket created",
      "loginToAttachFiles": "Log in to attach files. The ticket form itself can still be submitted without attachments.",
      "supportAttachmentLogin": "Log in to attach files",
      "languageChangeFailed": "Language change failed",
      "emailPlaceholder": "name@example.com",
      "usernamePlaceholder": "yourname",
      "campaignNameExample": "Summer hiring campaign",
      "adsNavigation": "Ads navigation",
      "campaigns": "Campaigns",
      "createCampaign": "Create campaign",
      "campaignName": "Campaign name",
      "objectiveAwareness": "Awareness",
      "objectiveTraffic": "Traffic",
      "objectiveEngagement": "Engagement",
      "objectiveLeads": "Leads",
      "objectiveMessages": "Messages",
      "budget": "Budget",
      "spent": "Spent",
      "placement": "Placement",
      "placementFeed": "Feed",
      "placementClips": "Clips",
      "placementPages": "Pages",
      "startDate": "Start",
      "endDate": "End",
      "saveDraft": "Save draft",
      "launchCampaign": "Launch campaign",
      "launchingCampaign": "Launching campaign",
      "savingDraft": "Saving draft",
      "campaignSaved": "Campaign saved",
      "campaignLaunched": "Campaign launched",
      "campaignUpdated": "Campaign updated",
      "campaignFailed": "Campaign failed",
      "formInvalid": "Add a campaign name and a valid budget.",
      "active": "Active",
      "paused": "Paused",
      "cancelled": "Cancelled",
      "completed": "Completed",
      "launch": "Launch",
      "cancelCampaign": "Cancel",
      "emptyCampaigns": "Your campaigns will appear here after you save the first campaign.",
      "emptyReport": "Reporting appears after the first impressions and clicks.",
      "loginRequiredTitle": "Log in required",
      "loginRequiredBody": "Log in to create and manage campaigns.",
      "untitledCampaign": "Untitled campaign",
      "noAudience": "Audience not configured",
      "campaignMetrics": "Campaign metrics",
      "impressions": "Impressions",
      "clicks": "Clicks",
      "ctr": "CTR",
      "languagePicker": "Language",
      "chooseLanguage": "Choose language",
      "closeLanguagePicker": "Close language picker",
      "availableLanguages": "Available languages",
      "changingLanguage": "Changing language",
      "displayNamePlaceholder": "Your name",
      "languagesPlaceholder": "English, Urdu, Hindi, Romanian",
      "topicHousing": "Housing",
      "topicDocuments": "Documents",
      "topicJobs": "Jobs",
      "topicSafety": "Safety",
      "initialSetupTitle": "Initial setup",
      "directRecipientRequired": "Add a username or email.",
      "startingDirectDiscussion": "Starting chat",
      "directDiscussionOpened": "Conversation opened",
      "directDiscussionStarted": "Conversation started",
      "directDiscussionFailed": "Conversation failed",
      "groupRoomMembersRequired": "A group chat needs at least two other people.",
      "groupRoomNameRequired": "Add a group chat name.",
      "creatingGroupRoom": "Creating group chat",
      "groupRoomCreated": "Group chat created",
      "groupRoomFailed": "Group chat failed",
      "selectConversationFirst": "Select a conversation first.",
      "sending": "Sending",
      "messageSent": "Message sent",
      "messageFailed": "Message failed",
      "send": "Send",
      "save": "Save",
      "directDiscussion": "Message someone",
      "directDiscussionMember": "Person to message",
      "directRecipientPlaceholder": "username or email",
      "startChat": "Start chat",
      "start": "Start",
      "groupRoom": "Group chat",
      "groupRoomName": "Group chat name",
      "groupNamePlaceholder": "Workmates, neighbours, family",
      "groupRoomMembers": "People in this chat",
      "groupMembersPlaceholder": "username, email, username",
      "create": "Create",
      "loadingConversations": "Loading conversations",
      "logInToUseMessages": "Log in to send and read messages.",
      "unableToLoadMessages": "Unable to load messages",
      "retryRequest": "Please retry the request.",
      "startConversationEmpty": "Start a conversation with one person.",
      "conversations": "Conversations",
      "selectConversation": "Select a conversation",
      "startConversationHint": "Select or start a conversation",
      "noConversationSelected": "No conversation selected",
      "chooseConversationBody": "Choose a conversation from the list or start a new one.",
      "loadingMessages": "Loading messages",
      "logInToLoadMessages": "Log in to see your messages.",
      "noMessagesYet": "No messages yet",
      "sendFirstMessage": "Send the first message in this conversation.",
      "writeMessage": "Write a message",
      "members": "members",
      "documentType": "Document type",
      "identity": "Identity",
      "workDocument": "Work document",
      "company": "Company",
      "subjectId": "Profile or page",
      "note": "Note",
      "operationsSections": "Operations sections",
      "contentType": "Content type",
      "post": "Post",
      "article": "Article",
      "movie": "Video",
      "articlePlaceholder": "First line is the title. Write the article below.",
      "postPlaceholder": "What do you want to talk about?",
      "photoPlaceholder": "Add a caption for your photo.",
      "moviePlaceholder": "Add a caption for your video.",
      "signInToPublish": "Log in to write a post",
      "loginToPublish": "Log in to publish posts and photos.",
      "writeArticleBeforePublishing": "Write the article before publishing.",
      "articleTitleMin": "The first line must be a title with at least 2 characters.",
      "articleSlugRequired": "The title must contain letters or numbers.",
      "publishingArticle": "Publishing article",
      "articlePublished": "Article published",
      "articleFailed": "Article failed",
      "writeSomethingOrAttachPhoto": "Write something or attach a photo.",
      "uploadingMedia": "Uploading media",
      "publishingMovie": "Publishing video",
      "publishing": "Publishing",
      "selectedPhotos": "Selected photos",
      "selectedMovie": "Selected video",
      "photo": "Photo",
      "visibility": "Visibility",
      "publish": "Publish",
      "friends": "Friends",
      "friendsOnly": "Friends only",
      "onlyMe": "Only me",
      "postQueuedForMedia": "Post published. Your photos will appear when they finish uploading.",
      "postPublished": "Post published",
      "postFailed": "Post failed",
      "chooseImageFiles": "Choose one or more image files.",
      "chooseVideoFile": "Choose a video file.",
      "moviePublished": "Video published",
      "movieFailed": "Video failed",
      "more": "More",
      "morePostActions": "More post actions",
      "moreCommentActions": "More comment actions",
      "deletePost": "Delete post",
      "deletePostConfirm": "Delete this post? This cannot be undone.",
      "deletingPost": "Deleting post",
      "postDeleteFailed": "Could not delete post",
      "deleteComment": "Delete comment",
      "deleteCommentConfirm": "Delete this comment? This cannot be undone.",
      "deletingComment": "Deleting comment",
      "commentDeleteFailed": "Could not delete comment",
      "deleteReply": "Delete reply",
      "deleteReplyConfirm": "Delete this reply? This cannot be undone.",
      "deletingReply": "Deleting reply",
      "replyDeleteFailed": "Could not delete reply",
      "reported": "Reported",
      "report": "Report",
      "like": "Like",
      "reactionLove": "Love",
      "reactionHaha": "Haha",
      "reactionWow": "Wow",
      "reactionSad": "Sad",
      "reactionAngry": "Angry",
      "chooseReaction": "Choose reaction",
      "comment": "Comment",
      "bookmark": "Bookmark",
      "sharePost": "Share post",
      "logInToShare": "Log in to share",
      "sharingPost": "Sharing post",
      "postShared": "Post shared to your feed",
      "shareFailed": "Share failed",
      "new2Member": "Member",
      "likes": "likes",
      "comments": "comments",
      "shares": "shares",
      "saves": "saves",
      "logInToLike": "Log in to like",
      "logInToMessage": "Message",
      "logInToAddFriend": "Add friend",
      "logInToFollow": "Log in to follow",
      "logInToJoin": "Log in to join",
      "logInToSave": "Log in to save",
      "logInToReport": "Log in to report",
      "logInToEventRsvp": "Log in to RSVP",
      "removingLike": "Removing like",
      "addingLike": "Adding like",
      "likeRemoved": "Like removed",
      "liked": "Liked",
      "likeFailed": "Like failed",
      "removingBookmark": "Removing bookmark",
      "savingBookmark": "Saving bookmark",
      "bookmarkRemoved": "Bookmark removed",
      "bookmarked": "Bookmarked",
      "bookmarkFailed": "Bookmark failed",
      "sendingReport": "Sending report",
      "reportedForReview": "Reported for review",
      "reportFailed": "Report failed",
      "loadingComments": "Loading comments",
      "commentsUnavailable": "Comments unavailable.",
      "noCommentsYetBody": "No comments yet. Start the conversation.",
      "postComment": "Post",
      "writeComment": "Write a comment",
      "logInToComment": "Log in to comment",
      "publishingComment": "Publishing comment",
      "commentPublished": "Comment published",
      "commentFailed": "Comment failed",
      "live": "Live",
      "loading": "Loading",
      "permission": "Permission",
      "needsAttention": "Needs attention",
      "waiting": "Waiting",
      "messageRemoved": "Message removed",
      "you": "You",
      "member": "Member",
      "clipboardUnavailable": "Clipboard unavailable",
      "criticalWorkflow": "Critical workflow",
      "backgroundWorkflow": "Background workflow",
      "generalSupport": "General support",
      "reportAbuse": "Report abuse",
      "accountAccess": "Account access",
      "privacyGdpr": "Privacy GDPR",
      "paymentsBilling": "Payments billing",
      "securityDisclosure": "Security disclosure",
      "legalRights": "Legal rights",
      "productFeedback": "Product feedback",
      "salesPartnership": "Sales partnership",
      "terms": "Terms",
      "partners": "Partners",
      "serviceStatus": "Status",
      "deleteItem": "Delete item",
      "clearHistory": "Clear history",
      "chooseItemFromView": "Choose an item from this view first.",
      "chooseMemberPageHashtag": "Choose a member, page or hashtag first.",
      "chooseGroupRequest": "Choose a group request first.",
      "chooseAdCampaign": "Choose an ad campaign first.",
      "chooseGroupFirst": "Choose a group first.",
      "createStory": "Create story",
      "storyTextPlaceholder": "Start with text",
      "shareStory": "Share to story",
      "storyPublished": "Story published",
      "storyFailed": "Story failed",
      "storyRequiresTextOrPhoto": "Add text or a photo for your story.",
      "addStoryFromComposer": "Add a photo or text story from the story composer.",
      "chooseMembershipPlan": "Choose one of the membership plans in the list first.",
      "chooseItemBeforeAction": "Choose an item before this action.",
      "chooseItemForSetting": "Choose an item first to apply this setting.",
      "actionNeedsSelectedItem": "This action needs a selected item in this view.",
      "selectCountry": "Select country",
      "selectIndustry": "Select industry",
      "selectInterests": "Select interests"
    }
  },
  "public": {
    "nav": {
      "home": "Home",
      "join": "Sign up",
      "login": "Log in",
      "support": "Support"
    },
    "hero": {
      "primary": "OpenSN"
    }
  },
  "auth": {
    "twoFactor": "Two-factor authentication"
  },
  "categoryLabels": {
    "groupCommunity": "Community",
    "groupLanguage": "Language",
    "groupSafety": "Safety",
    "groupTransport": "Transport",
    "groupWorkJobs": "Work and jobs",
    "pageTraining": "Training",
    "topicFamilyMembers": "Family members",
    "topicOnlineServicesAppointments": "Online services and appointments",
    "topicTaxHealthSocialSecurity": "Tax, health, and social security"
  },
  "errors": {
    "conflict": "This change conflicts with the current state.",
    "forbidden": "You do not have permission.",
    "notFound": "This item was not found.",
    "server": "Something went wrong.",
    "unauthorized": "Log in to continue.",
    "validation": "Check the fields and try again."
  },
  "errorMessages": {
    "invalid_credentials": "The email or password is incorrect."
  },
  "support": {}
};

const OPEN_SN_BASE_RO: I18nDictionary = {
  "localeCode": "ro-RO",
  "social": {
    "modules": {
      "ads": "Reclame",
      "articles": "Articole",
      "chat": "Messenger",
      "clips": "Video",
      "documents": "Documente",
      "events": "Evenimente",
      "feed": "Acasă",
      "groups": "Grupuri",
      "membership": "Abonament",
      "network": "Prieteni",
      "notifications": "Notificări",
      "ops": "Admin",
      "pages": "Pagini",
      "profile": "Profil",
      "search": "Căutare",
      "saved": "Salvate",
      "settings": "Setări",
      "support": "Suport",
      "wallet": "Portofel"
    },
    "ui": {
      "guestMember": "Musafir",
      "currentProfile": "Profil curent",
      "primaryNavigation": "Navigație principală",
      "socialSearch": "Caută",
      "searchPlaceholder": "Caută",
      "addNewPost": "Postare noua",
      "writePostCta": "Scrie o postare",
      "guestWelcomeTitle": "Bun venit",
      "createAccount": "Alatura-te",
      "invitePeople": "Invitatii",
      "profile": "Profil",
      "logOut": "Ieși din cont",
      "invites": "Invitatii",
      "membership": "Abonament",
      "verify": "Verifica",
      "stories": "Povesti",
      "events": "Evenimente",
      "groups": "Grupuri",
      "pages": "Pagini",
      "notifications": "Notificari",
      "clips": "Clipuri",
      "articles": "Articole",
      "documents": "Documente",
      "ads": "Reclame",
      "lists": "Liste",
      "workCommunitySupport": "Munca in Europa",
      "saferStartTitle": "OpenSN",
      "walletTools": "Portofel",
      "addFunds": "Adauga bani",
      "withdrawFunds": "Retrage bani",
      "sendMoney": "Trimite bani",
      "payNow": "Plateste acum",
      "languagesPrepared": "Limbi pregatite",
      "filterResults": "Filtreaza rezultatele",
      "clear": "Sterge",
      "cancel": "Anuleaza",
      "settingsSections": "Sectiuni setari",
      "settingsProfile": "Profil",
      "settingsPrivacy": "Confidentialitate",
      "settingsNotifications": "Notificari",
      "settingsEmail": "E-mail",
      "settingsPassword": "Parola",
      "settingsTwoFactor": "Autentificare in doi pasi",
      "settingsDisplay": "Afisare",
      "settingsDataDownload": "Descarcare date",
      "settingsAccount": "Cont",
      "settingsSubscriptions": "Abonamente",
      "settingsCreatorDashboard": "Panou pentru creatori",
      "settingsRequireLoginTitle": "Autentifica-te ca sa deschizi setarile",
      "settingsRequireLoginBody": "Profilul, confidentialitatea si setarile contului sunt salvate in contul tau.",
      "logInToContinueTitle": "Autentifica-te ca sa continui",
      "logInToContinueBody": "Foloseste contul tau ca sa publici, sa trimiti mesaje, sa salvezi continut si sa gestionezi setarile.",
      "adminSearchPlaceholder": "Cauta useri, postari sau inregistrari",
      "itemsPerPage": "Elemente pe pagina",
      "page": "Pagina",
      "previousPage": "Inapoi",
      "nextPage": "Inainte",
      "mediaDefaults": "Media implicita",
      "mediaDefaultsHelp": "Configureaza imaginile de profil si coperta folosite cand membrii nu au incarcat propriile imagini.",
      "defaultProfilePhotoLight": "Poza de profil implicita, tema luminoasa",
      "defaultProfilePhotoDark": "Poza de profil implicita, tema intunecata",
      "defaultCoverPhotoLight": "Coperta implicita, tema luminoasa",
      "defaultCoverPhotoDark": "Coperta implicita, tema intunecata",
      "createdAt": "Creat",
      "reason": "Motiv",
      "going": "Participa",
      "replies": "Raspunsuri",
      "pending": "In asteptare",
      "failed": "Esuat",
      "expired": "Expirat",
      "revoked": "Revocat",
      "accepted": "Acceptat",
      "blocked": "Blocat",
      "archived": "Arhivat",
      "resolved": "Rezolvat",
      "escalated": "Escaladat",
      "hashtags": "Hashtag-uri",
      "followers": "Urmaritori",
      "categories": "Categorii",
      "sortOrder": "Ordine",
      "moduleFeedLead": "Postari, comentarii si actualizari din comunitatea ta.",
      "moduleNetworkLead": "Invita contacte si vezi cine ti s-a alaturat.",
      "moduleChatLead": "Mesaje private cu persoane cunoscute.",
      "moduleGroupsLead": "Comunitati pentru sosire, munca, acte, locuire si drepturi in Europa.",
      "moduleEventsLead": "Evenimente online si locale, cu raspunsuri si participanti.",
      "modulePagesLead": "Gaseste pagini de persoane fizice, agentii de munca, angajatori, firme si servicii de sprijin.",
      "moduleDocumentsLead": "Documente",
      "moduleClipsLead": "Clipuri de la prieteni si pagini.",
      "moduleSettingsLead": "Setari",
      "moduleSupportLead": "Suport",
      "moduleWalletLead": "Portofel",
      "moduleMembershipLead": "Abonament",
      "moduleSearchLead": "Caută în OpenSN.",
      "moduleSavedLead": "Postari, linkuri si media salvate.",
      "moduleNotificationsLead": "Cereri de prietenie, raspunsuri, mesaje si alerte de siguranta.",
      "moduleAdsLead": "Reclame",
      "profilePhotoRemoved": "Scoate imaginea",
      "coverImageRemoved": "Scoate coperta",
      "uploadProfilePhoto": "Se incarca fotografia de profil",
      "uploadProfileMedia": "Se incarca fotografia de profil si coperta",
      "savingSettings": "Se salveaza setarile",
      "settingsSaved": "Setarile au fost salvate",
      "settingsRequestFailed": "Setarile nu au putut fi salvate",
      "chooseImageFile": "Foto",
      "chooseCoverImageFile": "Incarca coperta",
      "changeProfilePhoto": "Schimba fotografia de profil",
      "removeImage": "Scoate imaginea",
      "clearSelectedProfilePhoto": "Sterge fotografia de profil selectata",
      "changeCoverImage": "Schimba imaginea de coperta",
      "removeCover": "Scoate coperta",
      "clearSelectedCoverImage": "Incarca coperta",
      "displayName": "Nume afisat",
      "username": "Nume utilizator",
      "gender": "Gen",
      "selectGender": "Alege genul",
      "genderFemale": "Feminin",
      "genderMale": "Masculin",
      "genderNonBinary": "Non-binar",
      "genderSelfDescribed": "Auto-descris",
      "genderPreferNotToSay": "Prefer sa nu spun",
      "bio": "Descriere",
      "bioPlaceholder": "Gaseste comunitate, pagini verificate, ghidaj pentru munca, suport de incredere si informatii locale utile.",
      "currentCity": "Oras curent",
      "currentCityPlaceholder": "Munca in Europa",
      "workSector": "Domeniu de lucru",
      "publicProfile": "Profil public",
      "profileVisibility": "Vizibilitatea profilului",
      "messagesFrom": "Cine iti poate trimite mesaje",
      "friendListVisibility": "Vizibilitatea listei de prieteni",
      "friendActivityVisibility": "Vizibilitatea activitatii de prietenie",
      "everyone": "Toata lumea",
      "nobody": "Nimeni",
      "allowMessagesFromNetwork": "Chat: prieteni",
      "allowCommentsOnPosts": "Permite comentarii la postari",
      "searchableProfile": "Afiseaza profilul in cautare",
      "showOnlineStatus": "Afiseaza statusul activ",
      "blockedUserId": "Utilizator blocat",
      "optionalUserId": "ID utilizator optional",
      "emailDigest": "Rezumat prin e-mail",
      "pushNotifications": "Notificari",
      "friendRequestNotifications": "Cereri de prietenie",
      "friendAcceptedNotifications": "Cereri acceptate",
      "friendActivityNotifications": "Activitate prieteni",
      "mutualFriendNotifications": "Prieteni comuni",
      "friendCountOne": "prieten",
      "friendCountMany": "prieteni",
      "mutualFriendCountOne": "prieten comun",
      "mutualFriendCountMany": "prieteni comuni",
      "commentNotifications": "Comentarii si raspunsuri",
      "messageNotifications": "Mesaje",
      "safetyAlerts": "Siguranta",
      "friendRequestReceivedNotification": "Cerere de prietenie",
      "friendRequestAcceptedNotification": "Cerere de prietenie acceptata",
      "friendAddedFriendNotification": "Prietenie noua",
      "friendAddedFriendBody": "este acum prieten cu",
      "followNotification": "Urmarire noua",
      "notificationUnread": "Necitita",
      "markAllRead": "Marcheaza toate ca citite",
      "email": "E-mail",
      "productUpdates": "Actualizari produs",
      "communityDigest": "Rezumat comunitate",
      "currentPassword": "Parola",
      "newPassword": "Parola",
      "method": "Setari",
      "phone": "Telefon",
      "authenticatorApp": "Autentificare in doi pasi",
      "verificationCode": "Cod de verificare",
      "codeAfterSetup": "Cod dupa configurare",
      "language": "Limba",
      "theme": "Tema",
      "themeLight": "Luminoasa",
      "themeDark": "Intunecata",
      "themeSystem": "Sistem",
      "darkMode": "Mod intunecat",
      "autoplayMedia": "Redare automata media",
      "exportScope": "Ce date exporti",
      "allAccountData": "Toate datele contului",
      "timeline": "Postări",
      "posts": "Flux",
      "media": "Clipuri",
      "exportQueued": "Exportul a fost pus in coada",
      "sessions": "Sesiuni",
      "noActiveSessions": "Nu exista sesiuni active.",
      "typeDelete": "Parola: ȘTERGE",
      "deleteConfirmationToken": "ȘTERGE",
      "confirmPassword": "Parola",
      "deleteReason": "Motivul stergerii",
      "optionalReason": "Motiv optional",
      "loginBeforeSubmitting": "Autentifica-te inainte de trimitere",
      "submitting": "Se trimite",
      "saved": "Salvat",
      "savedItems": "Elemente salvate",
      "savedItem": "Element salvat",
      "submitFailed": "Trimiterea a esuat",
      "category": "Categorie",
      "loadingCategories": "Se incarca categoriile",
      "categoriesUnavailable": "Categoriile nu sunt disponibile",
      "noCategoriesConfigured": "Nu exista categorii configurate",
      "chooseCategory": "Alege categoria",
      "allCategories": "Toate categoriile",
      "objective": "Obiectiv",
      "traffic": "Trafic",
      "reach": "Acoperire",
      "message": "Mesaj",
      "messages": "Mesaje",
      "budgetEur": "Buget EUR",
      "audience": "Audienta",
      "audiencePlaceholder": "Tari, limbi, domenii de munca",
      "creative": "Reclame",
      "creativePlaceholder": "Reclame",
      "recipientId": "Trimite catre",
      "recipientPlaceholder": "nume de utilizator sau e-mail",
      "amountEur": "Suma in EUR",
      "paymentProvider": "Metoda de plata",
      "uploadedFileId": "Fisier video",
      "uploadedFilePlaceholder": "Alege sau lipeste fisierul video",
      "caption": "Descriere",
      "clipCaption": "Clipuri",
      "title": "Titlu",
      "slug": "Legatura personalizata",
      "excerpt": "Rezumat",
      "body": "Continut",
      "status": "Stare",
      "draft": "Ciorna",
      "published": "Publicat",
      "name": "Nume",
      "optionalSlug": "Legatura personalizata optionala",
      "description": "Gaseste comunitate, pagini verificate, ghidaj pentru munca, suport de incredere si informatii locale utile.",
      "privacy": "Confidentialitate",
      "public": "Public",
      "private": "Privat",
      "network": "Prieteni",
      "eventName": "Nume eveniment",
      "eventNamePlaceholder": "Adauga un nume clar pentru eveniment",
      "eventFormat": "Format eveniment",
      "inPersonEvent": "In persoana",
      "onlineEvent": "Online",
      "eventLocation": "Locatie",
      "eventLocationPlaceholder": "Loc, oras sau adresa",
      "eventLocationTbd": "Locatia va fi anuntata",
      "eventLink": "Link eveniment online",
      "eventLinkPlaceholder": "https://...",
      "eventDescriptionPlaceholder": "Spune ce se intampla, cine ar trebui sa vina si la ce sa se astepte.",
      "eventHosts": "Gazde",
      "eventHostsPlaceholder": "Nume de gazde sau pagini",
      "eventHostedBy": "Gazduit de",
      "eventCapacity": "Capacitate",
      "eventCapacityPlaceholder": "Limita optionala",
      "eventInvitesPlaceholder": "Nume, utilizatori sau emailuri",
      "eventDateTbd": "De anuntat",
      "eventRsvp": "Raspuns",
      "eventInterested": "Interesat",
      "eventGoing": "Particip",
      "eventCantGo": "Nu pot merge",
      "eventRsvpSaving": "Se salveaza raspunsul",
      "eventRsvpSaved": "Raspuns salvat",
      "eventRsvpFailed": "Raspunsul a esuat",
      "eventDetails": "Detalii eveniment",
      "eventsUnavailable": "Evenimentele nu sunt disponibile inca.",
      "createEvent": "Creeaza eveniment",
      "playbackActive": "Redare activa",
      "playbackActiveWithSound": "Redare activa cu sunet",
      "playMedia": "Porneste media",
      "pauseMedia": "Pauza media",
      "play": "Reda",
      "pause": "Pauza",
      "editProfile": "Editeaza profilul",
      "uploadAvatar": "Incarca fotografia de profil",
      "uploadCover": "Incarca coperta",
      "share": "Distribuie",
      "linkReady": "Legatura este gata",
      "refresh": "Reincarca",
      "search": "Caută",
      "loadMore": "Incarca mai mult",
      "quickActions": "Actiuni rapide",
      "done": "Salveaza",
      "actionFailed": "Actiunea a esuat",
      "copyingLink": "Se copiaza legatura",
      "linkCopied": "Legatura copiata",
      "loadingResults": "Se incarca rezultatele",
      "loginRequired": "Autentificare necesara",
      "loginToLoadView": "Autentifica-te ca sa vezi aceasta zona.",
      "unableToLoadView": "Nu am putut incarca aceasta zona.",
      "retry": "Incearca din nou",
      "goHome": "Acasa",
      "contactSupport": "Contacteaza suportul",
      "keepSubscription": "Pastreaza abonamentul",
      "featurePage": "Promoveaza pagina",
      "editItem": "Editeaza",
      "runNow": "Ruleaza acum",
      "disconnect": "Deconecteaza",
      "installApp": "Instaleaza aplicatia",
      "boostPost": "Promoveaza postarea",
      "logOutOtherDevices": "Delogheaza celelalte sesiuni",
      "rows": "Liste",
      "queues": "Cozi",
      "overview": "Prezentare",
      "users": "Utilizatori",
      "reports": "Rapoarte",
      "follows": "Relatii",
      "supportTickets": "Tichete suport",
      "openidProviders": "Furnizori OpenID",
      "mediaProcessing": "Procesare media",
      "dataExports": "Exporturi date",
      "accountDeletes": "Stergeri conturi",
      "opsGroupOverview": "Prezentare",
      "opsGroupContent": "Continut",
      "opsGroupCommunity": "Comunitate",
      "opsGroupEngagement": "Interactiuni",
      "opsGroupCommunication": "Comunicatii",
      "opsGroupModeration": "Moderare",
      "opsGroupMonetization": "Monetizare",
      "opsGroupConfiguration": "Configurare",
      "opsGroupOperations": "Operatiuni",
      "jobs": "Joburi",
      "deadLetters": "Mesaje esuate",
      "cron": "Programare",
      "outbox": "Mesaje trimise",
      "webhooks": "Apeluri web",
      "locks": "Lock-uri",
      "exports": "Exporturi",
      "deletes": "Stergeri",
      "retention": "Retentie",
      "roles": "Roluri",
      "section": "Setari",
      "subject": "Subiectul cererii de suport",
      "describeWhatHappened": "Raportare abuz",
      "chooseFiles": "Documente",
      "attachFiles": "Documente",
      "clearForm": "Goleste formularul",
      "ticketId": "ID cerere de suport",
      "supportRequestFailed": "Suport",
      "onboarding": "Configurare",
      "working": "Se lucreaza",
      "passwordResetComplete": "Parola a fost resetata",
      "recoveryEmailQueued": "Verifica emailul pentru linkul de resetare",
      "logInFirstSocialConsent": "Autentifica-te mai intai pentru a conecta un alt serviciu de autentificare.",
      "logInAfterSignupToFinishSetup": "Autentifica-te dupa crearea contului pentru finalizare.",
      "requestFailed": "Cererea a esuat",
      "invalidCredentials": "Emailul sau parola este incorectă.",
      "startingOpenId": "Se deschide autentificarea cu Google",
      "openIdProviderReady": "Autentificarea cu Google este pregatita",
      "openIdRequestFailed": "Autentificarea cu Google a esuat",
      "usernameMinLength": "Numele de utilizator trebuie sa aiba cel putin 3 caractere.",
      "checkingUsername": "Se verifica numele de utilizator",
      "usernameAlreadyUsed": "Numele de utilizator este deja folosit.",
      "usernameAvailable": "Numele de utilizator este disponibil.",
      "usernameCheckFailed": "Verificarea numelui de utilizator a esuat",
      "avatarRemovedOnboarding": "Avatarul va fi sters cand termini configurarea.",
      "originCountry": "Tara de origine",
      "currentCountry": "Tara curenta",
      "interests": "Interese",
      "password": "Parola",
      "resetPasswordWithToken": "Parola noua",
      "enterPassword": "Introdu parola",
      "hidePassword": "Ascunde parola",
      "showPassword": "Arata parola",
      "resetToken": "Coduri de recuperare",
      "check": "Verifica",
      "finishOnboarding": "Finalizeaza configurarea",
      "continue": "Continua",
      "forgotPassword": "Ai uitat parola?",
      "sendResetLink": "Trimite linkul de resetare",
      "resetPassword": "Reseteaza parola",
      "signUp": "Inregistrare",
      "alreadyHaveAccount": "Ai deja cont?",
      "skipOptionalSetup": "Sari peste configurarea optionala",
      "profileSettings": "Setari profil",
      "backToLogin": "Inapoi la autentificare",
      "filterInvitedPeople": "Filtreaza persoanele invitate",
      "invitedPeople": "Persoane invitate",
      "invitedPeopleEmpty": "Persoanele invitate vor aparea aici dupa ce trimiti o invitatie sau copiezi o legatura de inregistrare.",
      "invitedPeopleShortEmpty": "Persoanele invitate vor aparea aici.",
      "addEmailOrPhone": "Adauga o adresa de e-mail sau un numar de telefon.",
      "sendingInvite": "Se trimite invitatia",
      "inviteQueuedLinkReady": "Invitatia este in coada si linkul este pregatit",
      "inviteQueued": "Invitatia este in coada",
      "inviteRequestFailed": "Cererea de invitatie a esuat",
      "creatingRegistrationLink": "Se creeaza linkul de inregistrare",
      "inviteLinkMissing": "Lipseste linkul de invitatie",
      "registrationLinkCopied": "Linkul de inregistrare a fost copiat",
      "inviteLinkRequestFailed": "Cererea pentru linkul de invitatie a esuat",
      "registrationLink": "Legatura de inregistrare",
      "sendInvite": "Trimite invitatia",
      "copyRegistrationLink": "Copiaza linkul de inregistrare",
      "copyLink": "Copiaza legatura",
      "acceptedBy": "Acceptata de",
      "openProfile": "Deschide profilul",
      "about": "Despre",
      "walletEmpty": "Activitatea din portofel va aparea dupa prima tranzactie.",
      "feedEmpty": "Fluxul tau se va umple cu postari de la prieteni, grupuri si pagini.",
      "feedFilters": "Filtre pentru feed",
      "searchEmpty": "Folosește câmpul de mai sus pentru a căuta.",
      "searchNoResultsTitle": "Nu exista rezultate",
      "searchNoResultsBody": "Nu am gasit nimic pentru aceasta cautare.",
      "notificationsEmpty": "Cererile de prietenie, raspunsurile, mesajele si alertele de siguranta vor aparea aici.",
      "savedEmpty": "Postarile, linkurile si media salvate vor aparea aici.",
      "noGroupsYet": "Nu exista grupuri inca",
      "noEventsYet": "Nu exista evenimente inca",
      "noPagesYet": "Nu exista pagini inca",
      "noFriendsYet": "Nu exista prieteni inca",
      "noDocumentsYet": "Nu exista documente inca",
      "noPhotosYet": "Nu exista fotografii inca",
      "noArticlesYet": "Nu exista articole inca",
      "groupsEmpty": "Grupurile de munca, sosire, acte, locuire si siguranta potrivite acestei sectiuni vor aparea aici.",
      "eventsEmpty": "Evenimentele online si locale potrivite acestei sectiuni vor aparea aici.",
      "pagesEmpty": "Paginile de persoane fizice, agentii, angajatori si firme potrivite acestei sectiuni vor aparea aici.",
      "documentsEmpty": "Documentele atasate postarilor vor aparea aici.",
      "clipsEmpty": "Clipurile video vor aparea aici dupa ce membrii publica video-uri.",
      "photos": "Fotografii",
      "photosEmpty": "Fotografiile vor aparea aici dupa ce sunt incarcate.",
      "articlesEmpty": "Articolele vor aparea aici dupa ce membrii publica texte mai lungi.",
      "friendsEmpty": "Prietenii vor aparea aici dupa ce conexiunile sunt acceptate.",
      "listsEmpty": "Listele salvate si relatiile sociale vor aparea aici.",
      "adsEmpty": "Campaniile si rapoartele de reclame vor aparea aici dupa configurare.",
      "opsEmpty": "Inregistrarile operationale vor aparea aici dupa ce ruleaza cozi, joburi sau procese de fundal.",
      "profileEmpty": "Continutul profilului va aparea aici dupa ce exista postari, media sau interactiuni.",
      "profileAboutEmpty": "Detaliile de profil vor aparea aici dupa ce sunt adaugate.",
      "profileTimelineEmpty": "Postarile vor aparea aici dupa ce sunt publicate.",
      "profileNotFound": "Profil indisponibil",
      "profileNotFoundBody": "Acest profil nu exista sau nu este disponibil.",
      "membershipPlansEmpty": "Momentan nu exista planuri de abonament disponibile.",
      "membershipCurrentEmpty": "Nu ai inca un abonament activ.",
      "membershipCheckoutEmpty": "Alege un plan de abonament inainte de plata.",
      "genericEmpty": "Nu exista nimic aici inca.",
      "selectPlan": "Alege planul",
      "startCheckout": "Continua la plata",
      "membershipCheckoutStarted": "Plata pentru abonament a fost pornita",
      "currentMembership": "Abonamentul curent",
      "noPostsYet": "Nu exista postari inca",
      "startWithQuery": "Incepe cu o cautare",
      "noConversationsYet": "Nu exista conversatii inca",
      "newMessage": "Mesaj nou",
      "allClear": "Totul este in regula",
      "noActivityYet": "Nu exista activitate inca",
      "nothingHereYet": "Nu exista nimic aici inca",
      "open": "Deschide",
      "directoryItem": "Element",
      "ready": "Pregatit",
      "join": "Alatura-te",
      "follow": "Urmareste",
      "joined": "Te-ai alaturat",
      "following": "Urmaresti",
      "hashtagFollowed": "Hashtag urmarit",
      "termsAccepted": "Accept Termenii.",
      "privacyAccepted": "Accept politica de confidentialitate.",
      "termsConsentIntro": "Sunt de acord cu",
      "termsConsentJoin": "si",
      "privacyPolicy": "Politica de confidentialitate",
      "marketingConsentText": "Vreau actualizari de produs si marketing descrise in",
      "cookies": "Cookie-uri",
      "code": "Cod",
      "methodId": "ID metoda",
      "provider": "Serviciu de autentificare",
      "initialSetupSteps": "Pasi configurare initiala",
      "recommendations": "Recomandari",
      "removeAvatar": "Sterge avatarul",
      "avatarWillBeRemoved": "Avatarul va fi sters",
      "clearSelectedAvatar": "Sterge avatarul selectat",
      "headline": "Titlu scurt",
      "headlinePlaceholder": "Lucrator, student, fondator, parinte",
      "bioSupportPlaceholder": "Spune ce suport, munca sau comunitate cauti",
      "languages": "Limbi",
      "interestsAndHashtags": "Interese si hashtag-uri",
      "logIn": "Autentificare",
      "continueWithOpenId": "Continua cu Google",
      "emailToken": "Token de confirmare",
      "requestCompleted": "Cerere finalizata",
      "createSupportTicket": "Trimite cererea de suport",
      "ticketCreated": "Cerere de suport creata",
      "loginToAttachFiles": "Autentifica-te pentru atasamente. Formularul poate fi trimis fara fisiere.",
      "supportAttachmentLogin": "Autentifica-te pentru atasamente",
      "languageChangeFailed": "Schimbarea limbii a esuat",
      "emailPlaceholder": "nume@exemplu.ro",
      "usernamePlaceholder": "numele_tau",
      "campaignNameExample": "Campanie recrutare vara",
      "adsNavigation": "Navigare reclame",
      "campaigns": "Campanii",
      "createCampaign": "Creeaza campanie",
      "campaignName": "Nume campanie",
      "objectiveAwareness": "Vizibilitate",
      "objectiveTraffic": "Trafic",
      "objectiveEngagement": "Interactiuni",
      "objectiveLeads": "Lead-uri",
      "objectiveMessages": "Mesaje",
      "budget": "Buget",
      "spent": "Cheltuit",
      "placement": "Plasament",
      "placementFeed": "Flux",
      "placementClips": "Clipuri",
      "placementPages": "Pagini",
      "startDate": "Data start",
      "endDate": "Final",
      "saveDraft": "Salveaza ciorna",
      "launchCampaign": "Lanseaza campania",
      "launchingCampaign": "Se lanseaza campania",
      "savingDraft": "Se salveaza draftul",
      "campaignSaved": "Campanie salvata",
      "campaignLaunched": "Campanie lansata",
      "campaignUpdated": "Campanie actualizata",
      "campaignFailed": "Campania a esuat",
      "formInvalid": "Completeaza numele campaniei si un buget valid.",
      "active": "Activ",
      "paused": "Pauzata",
      "cancelled": "Anulata",
      "completed": "Finalizata",
      "launch": "Lanseaza",
      "cancelCampaign": "Anuleaza",
      "emptyCampaigns": "Campaniile tale vor aparea aici dupa ce salvezi prima campanie.",
      "emptyReport": "Raportarea apare dupa primele impresii si clickuri.",
      "loginRequiredTitle": "Autentifica-te ca sa continui",
      "loginRequiredBody": "Intra in cont pentru a crea si gestiona campanii.",
      "untitledCampaign": "Campanie fara nume",
      "noAudience": "Audienta neconfigurata",
      "campaignMetrics": "Metrici campanie",
      "impressions": "Impresii",
      "clicks": "Clickuri",
      "ctr": "Rata clickurilor",
      "languagePicker": "Limba",
      "chooseLanguage": "Alege limba",
      "closeLanguagePicker": "Inchide selectorul de limba",
      "availableLanguages": "Limbi disponibile",
      "changingLanguage": "Se schimba limba",
      "displayNamePlaceholder": "Numele tau",
      "languagesPlaceholder": "Engleză, Urdu, Hindi, Română",
      "topicHousing": "Locuire",
      "topicDocuments": "Documente",
      "topicJobs": "Joburi",
      "topicSafety": "Siguranta",
      "initialSetupTitle": "Configurare initiala",
      "directRecipientRequired": "Adauga numele de utilizator sau e-mailul persoanei.",
      "startingDirectDiscussion": "Se porneste mesajul",
      "directDiscussionOpened": "Conversatie deschisa",
      "directDiscussionStarted": "Conversatie pornita",
      "directDiscussionFailed": "Conversatia nu a putut fi pornita",
      "groupRoomMembersRequired": "Un grup de chat are nevoie de cel putin doua persoane.",
      "groupRoomNameRequired": "Adauga numele grupului de chat.",
      "creatingGroupRoom": "Se creeaza grupul de chat",
      "groupRoomCreated": "Grup de chat creat",
      "groupRoomFailed": "Grupul de chat nu a putut fi creat",
      "selectConversationFirst": "Selecteaza intai o conversatie.",
      "sending": "Se trimite",
      "messageSent": "Mesaj trimis",
      "messageFailed": "Mesajul a esuat",
      "send": "Trimite",
      "save": "Salveaza",
      "directDiscussion": "Mesaj catre o persoana",
      "directDiscussionMember": "Persoana care primeste mesajul",
      "directRecipientPlaceholder": "nume de utilizator sau e-mail",
      "startChat": "Porneste chatul",
      "start": "Porneste",
      "groupRoom": "Grup de chat",
      "groupRoomName": "Nume grup de chat",
      "groupNamePlaceholder": "Nume grup",
      "groupRoomMembers": "Persoane in acest chat",
      "groupMembersPlaceholder": "nume de utilizator, e-mail, nume de utilizator",
      "create": "Creeaza",
      "loadingConversations": "Se incarca conversatiile",
      "logInToUseMessages": "Autentifica-te ca sa trimiti si sa citesti mesaje.",
      "unableToLoadMessages": "Mesajele nu pot fi incarcate",
      "retryRequest": "Reincearca cererea.",
      "startConversationEmpty": "Porneste o conversatie cu o persoana.",
      "conversations": "Conversatii",
      "selectConversation": "Selecteaza o conversatie",
      "startConversationHint": "Selecteaza sau porneste o conversatie",
      "noConversationSelected": "Nicio conversatie selectata",
      "chooseConversationBody": "Alege o conversatie din lista sau porneste una noua.",
      "loadingMessages": "Se incarca mesajele",
      "logInToLoadMessages": "Autentifica-te ca sa vezi mesajele tale.",
      "noMessagesYet": "Nu exista mesaje inca",
      "sendFirstMessage": "Trimite primul mesaj in aceasta conversatie.",
      "writeMessage": "Scrie un mesaj",
      "members": "membri",
      "documentType": "Tip document",
      "identity": "Identitate",
      "workDocument": "Document de munca",
      "company": "Companie",
      "subjectId": "Profil sau pagina",
      "note": "Nota",
      "operationsSections": "Sectiuni operatiuni",
      "contentType": "Tip continut",
      "post": "Postare",
      "article": "Articol",
      "movie": "Video",
      "articlePlaceholder": "Prima linie este titlul. Scrie articolul dedesubt.",
      "postPlaceholder": "Despre ce vrei sa vorbesti?",
      "photoPlaceholder": "Adauga o descriere pentru fotografie.",
      "moviePlaceholder": "Adauga o descriere pentru video.",
      "signInToPublish": "Autentifica-te ca sa scrii o postare",
      "loginToPublish": "Autentifica-te ca sa publici postari si fotografii.",
      "writeArticleBeforePublishing": "Scrie articolul inainte de publicare.",
      "articleTitleMin": "Prima linie trebuie sa fie un titlu cu cel putin 2 caractere.",
      "articleSlugRequired": "Titlul trebuie sa contina litere sau cifre.",
      "publishingArticle": "Se publica articolul",
      "articlePublished": "Articol publicat",
      "articleFailed": "Articolul a esuat",
      "writeSomethingOrAttachPhoto": "Scrie ceva sau ataseaza o fotografie.",
      "uploadingMedia": "Se incarca media",
      "publishingMovie": "Se publica video",
      "publishing": "Se publica",
      "selectedPhotos": "Fotografii selectate",
      "selectedMovie": "Video selectat",
      "photo": "Foto",
      "visibility": "Vizibilitate",
      "publish": "Publica",
      "friends": "Prieteni",
      "friendsOnly": "Doar pentru prieteni",
      "onlyMe": "Doar eu",
      "postQueuedForMedia": "Postarea a fost publicata. Fotografiile apar dupa ce se incarca.",
      "postPublished": "Postare publicata",
      "postFailed": "Postarea a esuat",
      "chooseImageFiles": "Alege unul sau mai multe fisiere imagine.",
      "chooseVideoFile": "Alege un fisier video.",
      "moviePublished": "Video publicat",
      "movieFailed": "Video-ul a esuat",
      "more": "Mai mult",
      "morePostActions": "Mai multe actiuni pentru postare",
      "moreCommentActions": "Mai multe actiuni pentru comentariu",
      "deletePost": "Sterge postarea",
      "deletePostConfirm": "Stergi aceasta postare? Actiunea nu poate fi anulata.",
      "deletingPost": "Se sterge postarea",
      "postDeleteFailed": "Postarea nu a putut fi stearsa",
      "deleteComment": "Sterge comentariul",
      "deleteCommentConfirm": "Stergi acest comentariu? Actiunea nu poate fi anulata.",
      "deletingComment": "Se sterge comentariul",
      "commentDeleteFailed": "Comentariul nu a putut fi sters",
      "deleteReply": "Sterge raspunsul",
      "deleteReplyConfirm": "Stergi acest raspuns? Actiunea nu poate fi anulata.",
      "deletingReply": "Se sterge raspunsul",
      "replyDeleteFailed": "Raspunsul nu a putut fi sters",
      "reported": "Raportat",
      "report": "Raporteaza",
      "like": "Apreciaza",
      "reactionLove": "Iubesc",
      "reactionHaha": "Haha",
      "reactionWow": "Uau",
      "reactionSad": "Trist",
      "reactionAngry": "Furios",
      "chooseReaction": "Alege reactia",
      "comment": "Comenteaza",
      "bookmark": "Salveaza",
      "sharePost": "Distribuie postarea",
      "logInToShare": "Autentifica-te pentru a distribui",
      "sharingPost": "Se distribuie postarea",
      "postShared": "Postarea a fost distribuita in fluxul tau",
      "shareFailed": "Distribuirea a esuat",
      "new2Member": "Membru",
      "likes": "aprecieri",
      "comments": "comentarii",
      "shares": "distribuiri",
      "saves": "salvari",
      "logInToLike": "Autentifica-te pentru a aprecia",
      "logInToMessage": "Mesaj",
      "logInToAddFriend": "Adauga",
      "logInToFollow": "Autentifica-te pentru a urmari",
      "logInToJoin": "Autentifica-te pentru a te alatura",
      "logInToSave": "Autentifica-te pentru a salva",
      "logInToReport": "Autentifica-te pentru a raporta",
      "logInToEventRsvp": "Autentifica-te pentru a raspunde",
      "removingLike": "Se scoate aprecierea",
      "addingLike": "Se adauga aprecierea",
      "likeRemoved": "Apreciere scoasa",
      "liked": "Apreciat",
      "likeFailed": "Aprecierea a esuat",
      "removingBookmark": "Se scoate salvarea",
      "savingBookmark": "Se salveaza",
      "bookmarkRemoved": "Salvare scoasa",
      "bookmarked": "Salvat",
      "bookmarkFailed": "Salvarea a esuat",
      "sendingReport": "Se trimite raportarea",
      "reportedForReview": "Raportat pentru verificare",
      "reportFailed": "Raportarea a esuat",
      "loadingComments": "Se incarca comentariile",
      "commentsUnavailable": "Comentariile nu sunt disponibile.",
      "noCommentsYetBody": "Nu exista comentarii inca. Porneste conversatia.",
      "postComment": "Posteaza",
      "writeComment": "Scrie un comentariu",
      "logInToComment": "Autentifica-te pentru a comenta",
      "publishingComment": "Se publica comentariul",
      "commentPublished": "Comentariu publicat",
      "commentFailed": "Comentariul a esuat",
      "live": "In direct",
      "loading": "Se incarca",
      "permission": "Permisiune",
      "needsAttention": "Necesita atentie",
      "waiting": "In asteptare",
      "messageRemoved": "Mesaj sters",
      "you": "Tu",
      "member": "Membru",
      "clipboardUnavailable": "Clipboard indisponibil",
      "criticalWorkflow": "Workflow critic",
      "backgroundWorkflow": "Workflow de fundal",
      "generalSupport": "Suport general",
      "reportAbuse": "Raporteaza abuz",
      "accountAccess": "Acces cont",
      "privacyGdpr": "Confidentialitate GDPR",
      "paymentsBilling": "Plati si facturare",
      "securityDisclosure": "Raportare securitate",
      "legalRights": "Drepturi legale",
      "productFeedback": "Opinii despre produs",
      "salesPartnership": "Vanzari si parteneriate",
      "terms": "Termeni",
      "partners": "Parteneri",
      "serviceStatus": "Stare serviciu",
      "deleteItem": "Sterge element",
      "clearHistory": "Sterge istoricul",
      "chooseItemFromView": "Alege mai intai un element din aceasta sectiune.",
      "chooseMemberPageHashtag": "Alege mai intai un membru, o pagina sau un hashtag.",
      "chooseGroupRequest": "Alege intai o cerere de grup.",
      "chooseAdCampaign": "Alege intai o campanie de reclame.",
      "chooseGroupFirst": "Alege intai un grup.",
      "createStory": "Creeaza poveste",
      "storyTextPlaceholder": "Incepe cu text",
      "shareStory": "Distribuie in poveste",
      "storyPublished": "Poveste publicata",
      "storyFailed": "Povestea a esuat",
      "storyRequiresTextOrPhoto": "Adauga text sau o fotografie pentru poveste.",
      "addStoryFromComposer": "Adauga o fotografie sau o poveste text din editorul de povesti.",
      "chooseMembershipPlan": "Alege intai un plan de abonament din lista.",
      "chooseItemBeforeAction": "Alege un element inainte de aceasta actiune.",
      "chooseItemForSetting": "Alege intai un element pentru aceasta setare.",
      "actionNeedsSelectedItem": "Aceasta actiune are nevoie de un element selectat in aceasta sectiune.",
      "selectCountry": "Selecteaza tara",
      "selectIndustry": "Selecteaza industria",
      "selectInterests": "Selecteaza interesele"
    }
  },
  "public": {
    "nav": {
      "home": "Acasă",
      "join": "Înscrie-te",
      "login": "Intră în cont",
      "support": "Suport"
    },
    "hero": {
      "primary": "OpenSN"
    }
  },
  "auth": {
    "twoFactor": "Autentificare în doi pași"
  },
  "categoryLabels": {
    "groupCommunity": "Comunitate",
    "groupLanguage": "Limbă",
    "groupSafety": "Siguranță",
    "groupTransport": "Transport",
    "groupWorkJobs": "Muncă și joburi",
    "pageTraining": "Training",
    "topicFamilyMembers": "Membri ai familiei",
    "topicOnlineServicesAppointments": "Servicii online și programări",
    "topicTaxHealthSocialSecurity": "Taxe, sănătate și asigurări sociale"
  },
  "errors": {
    "conflict": "Această schimbare intră în conflict cu starea curentă.",
    "forbidden": "Nu ai permisiune.",
    "notFound": "Elementul nu a fost găsit.",
    "server": "A apărut o eroare.",
    "unauthorized": "Intră în cont pentru a continua.",
    "validation": "Verifică câmpurile și încearcă din nou."
  },
  "errorMessages": {
    "invalid_credentials": "Emailul sau parola este incorectă."
  },
  "support": {}
};

const OPEN_SN_BASE_DICTIONARIES: Record<string, I18nDictionary> = {
  en: OPEN_SN_BASE_EN,
  ro: OPEN_SN_BASE_RO
};

export function openSNBaseDictionary(localeCode: string): I18nDictionary {
  return cloneDictionary(resolveBaseDictionary(localeCode));
}

export function extendOpenSNDictionary(localeCode: string, extension: OpenSNDictionaryExtension | I18nDictionary | null | undefined): I18nDictionary {
  return mergeExtension(resolveBaseDictionary(localeCode), extension) as I18nDictionary;
}

function resolveBaseDictionary(localeCode: string): I18nDictionary {
  const normalized = localeCode.toLowerCase();
  const shortLocale = normalized.split("-")[0] ?? normalized;
  return OPEN_SN_BASE_DICTIONARIES[normalized] ?? OPEN_SN_BASE_DICTIONARIES[shortLocale] ?? OPEN_SN_BASE_EN;
}

function cloneDictionary(dictionary: I18nDictionary): I18nDictionary {
  return mergeExtension(dictionary, null) as I18nDictionary;
}

function mergeExtension(base: Record<string, unknown>, extension: unknown): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  Object.entries(base).forEach(([key, value]) => {
    result[key] = cloneValue(value);
  });
  if (!isRecord(extension)) return result;
  Object.entries(extension).forEach(([key, value]) => {
    const current = result[key];
    if (isRecord(current) && isRecord(value)) {
      result[key] = mergeExtension(current, value);
      return;
    }
    result[key] = cloneValue(value);
  });
  return result;
}

function cloneValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(cloneValue);
  if (isRecord(value)) return mergeExtension(value, null);
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
